import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { Goal } from '@/types/goal';
import { DailyReflection, ReflectionFormData } from '@/types/reflection';
import { reportError } from '@/lib/error-reporting';
import {
  isReflectionActivated,
  calculateReflectionStreak,
  isSkippedToday,
  markSkippedToday,
} from '@/lib/reflection-utils';
import { format } from 'date-fns';

const PAGE_SIZE = 20;

function mapRow(row: Record<string, unknown>): DailyReflection {
  return {
    id: row.id as string,
    userId: row.user_id as string,
    reflectionDate: row.reflection_date as string,
    energized: row.energized as string | null,
    drained: row.drained as string | null,
    changeAction: row.change_action as string | null,
    valuesTags: (row.values_tags as Record<string, unknown>) ?? {},
    sentimentScore: row.sentiment_score as number | null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

export function useReflections(goals: Goal[]) {
  const { user } = useAuth();
  const { effectiveUserId } = useImpersonation();
  const userId = effectiveUserId || user?.id;
  const today = format(new Date(), 'yyyy-MM-dd');

  const [todayReflection, setTodayReflection] = useState<DailyReflection | null>(null);
  const [reflections, setReflections] = useState<DailyReflection[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSkipped, setIsSkipped] = useState(() => isSkippedToday(today));

  const isActivated = useMemo(() => isReflectionActivated(goals), [goals]);

  const reflectionStreak = useMemo(() => {
    const dates = reflections.map((r) => r.reflectionDate);
    // Today's reflection might not be in reflections[] yet if it was just saved
    if (todayReflection && !dates.includes(todayReflection.reflectionDate)) {
      dates.unshift(todayReflection.reflectionDate);
    }
    // Dates should already be desc from DB order, but ensure sorting
    dates.sort((a, b) => b.localeCompare(a));
    return calculateReflectionStreak(dates);
  }, [reflections, todayReflection]);

  // Fetch today's reflection + recent history
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetch() {
      try {
        // Fetch today's reflection
        const { data: todayData, error: todayError } = await supabase
          .from('daily_reflections')
          .select('*')
          .eq('user_id', userId!)
          .eq('reflection_date', today)
          .maybeSingle();

        if (todayError) throw todayError;

        // Fetch recent reflections for history + streak
        const { data: historyData, error: historyError } = await supabase
          .from('daily_reflections')
          .select('*')
          .eq('user_id', userId!)
          .order('reflection_date', { ascending: false })
          .limit(PAGE_SIZE + 1);

        if (historyError) throw historyError;

        if (cancelled) return;

        setTodayReflection(todayData ? mapRow(todayData) : null);

        const rows = historyData || [];
        setHasMore(rows.length > PAGE_SIZE);
        setReflections(rows.slice(0, PAGE_SIZE).map(mapRow));
      } catch (error) {
        reportError(error, 'reflections', { action: 'fetch' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetch();
    return () => { cancelled = true; };
  }, [userId, today]);

  const saveReflection = useCallback(
    async (data: ReflectionFormData) => {
      if (!userId) return;

      // Optimistic update
      const optimistic: DailyReflection = {
        id: todayReflection?.id || crypto.randomUUID(),
        userId,
        reflectionDate: today,
        energized: data.energized || null,
        drained: data.drained || null,
        changeAction: data.changeAction || null,
        valuesTags: {},
        sentimentScore: null,
        createdAt: todayReflection?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      const prevToday = todayReflection;
      const prevReflections = reflections;
      setTodayReflection(optimistic);

      // Update reflections list
      setReflections((prev) => {
        const withoutToday = prev.filter((r) => r.reflectionDate !== today);
        return [optimistic, ...withoutToday];
      });

      try {
        const { data: row, error } = await supabase
          .from('daily_reflections')
          .upsert(
            {
              user_id: userId,
              reflection_date: today,
              energized: data.energized || null,
              drained: data.drained || null,
              change_action: data.changeAction || null,
            },
            { onConflict: 'user_id,reflection_date' }
          )
          .select()
          .single();

        if (error) throw error;

        const mapped = mapRow(row);
        setTodayReflection(mapped);
        setReflections((prev) => {
          const withoutToday = prev.filter((r) => r.reflectionDate !== today);
          return [mapped, ...withoutToday];
        });

        // Fire-and-forget: tag reflection with values alignment
        supabase.functions
          .invoke('tag-reflection-values', {
            body: { reflectionId: mapped.id },
          })
          .catch(() => {});
      } catch (error) {
        // Rollback
        setTodayReflection(prevToday);
        setReflections(prevReflections);
        reportError(error, 'reflections', { action: 'save' });
        throw error;
      }
    },
    [userId, today, todayReflection, reflections]
  );

  const skipToday = useCallback(() => {
    markSkippedToday(today);
    setIsSkipped(true);
  }, [today]);

  const loadMore = useCallback(async () => {
    if (!userId || reflections.length === 0) return;

    const lastDate = reflections[reflections.length - 1].reflectionDate;

    try {
      const { data, error } = await supabase
        .from('daily_reflections')
        .select('*')
        .eq('user_id', userId)
        .lt('reflection_date', lastDate)
        .order('reflection_date', { ascending: false })
        .limit(PAGE_SIZE + 1);

      if (error) throw error;

      const rows = data || [];
      setHasMore(rows.length > PAGE_SIZE);
      setReflections((prev) => [
        ...prev,
        ...rows.slice(0, PAGE_SIZE).map(mapRow),
      ]);
    } catch (error) {
      reportError(error, 'reflections', { action: 'load-more' });
    }
  }, [userId, reflections]);

  return {
    todayReflection,
    reflectionStreak,
    isActivated,
    isSkipped,
    loading,
    saveReflection,
    skipToday,
    reflections,
    hasMore,
    loadMore,
  };
}
